<script setup lang="ts">
import { computed, ref } from 'vue';

import type { IChangeDepartmentForm, IDepartmentItem } from '@modules/affiliates-approved/types';
import type { FormFlattenKey, FormFlattenValue, FormSchema, VFormRef } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { TRANSLATION_GROUP_AFFILIATES_APPROVED } from '@modules/affiliates-approved/constants';
import { useChangeDepartmentMutation } from '@modules/affiliates-approved/mutations';
import { useAffiliatesDepartmentListQuery } from '@modules/affiliates-approved/queries';
import { TRANSLATION_GROUP_AFFILIATES_PENDING } from '@modules/affiliates-pending/constants';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EAasmState } from '@modules/core/enums';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

import type { IChangeDepartmentDialogProps } from './types';

const props = withDefaults(defineProps<IChangeDepartmentDialogProps>(), {
  selectedIds: () => [],
  filterId: 0,
  page: 1,
});

const visible = defineModel<boolean>('visible');

const showDepartmentWarning = ref(false);
const isQueryEnabled = ref(false);
const form = ref<VFormRef>();

const { getTranslation } = useI18n();
const { confirmEdit } = useConfirmDialogs();

const { data: departments, isLoading: isDepartmentsLoading } = useAffiliatesDepartmentListQuery(isQueryEnabled);

const {
  isLoading: isSaveLoading,
  errors,
  clearError,
  mutateAsync,
} = useChangeDepartmentMutation();

const departmentWithoutDefault = computed<IDepartmentItem[] | undefined>(() => departments.value?.filter(({ aasm_state }) => aasm_state === EAasmState.STANDARD));

const departmentWarning = computed<string | undefined>(() =>
  showDepartmentWarning.value
    ? getTranslation(TRANSLATION_GROUP_AFFILIATES_PENDING, 'ACCEPT_SAVE_DEPARTMENT_WARNING').value
    : undefined,
);

const formSchema = computed<FormSchema<IChangeDepartmentForm, never, {
  department_id: {
    selectOptionValue: 'id';
    selectOptionLabel: 'name';
  };
}>>(() => ({
  department_id: {
    type: EFormInputTypes.SELECT,
    options: departmentWithoutDefault.value ?? [],
    label: getTranslation(TRANSLATION_GROUP_AFFILIATES_APPROVED, 'CHOOSE_DEPARTMENT_LABEL').value,
    optionValue: 'id',
    optionLabel: 'name',
    required: true,
    loading: isDepartmentsLoading.value,
    description: departmentWarning.value,
    descriptionSeverity: 'warn',
  },
}));

function onShow(): void {
  isQueryEnabled.value = true;
}

function onHide(): void {
  visible.value = false;
  showDepartmentWarning.value = false;
}

function onChange<
  T extends IChangeDepartmentForm,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  showDepartmentWarning.value = key === 'department_id' && !!value;
}

async function onSubmit({ department_id }: IChangeDepartmentForm): Promise<void> {
  confirmEdit({
    event,
    header: getTranslation(TRANSLATION_GROUP_AFFILIATES_APPROVED, 'CONFIRM_DEPARTMENT_TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_AFFILIATES_APPROVED, 'CONFIRM_DEPARTMENT_QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_APPROVED, 'CONFIRM_DEPARTMENT_OK').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_APPROVED, 'CONFIRM_DEPARTMENT_CANCEL').value,
    accept: () => {
      void mutateAsync({
        department_id,
        affiliate_ids: props.selectedIds,
        segment_id: props.filterId,
        page: props.page,
      }).then(onHide);
    },
  });
}

function confirmChanging(): void {
  form.value?.submit();
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_APPROVED, 'CHOOSE_DEPARTMENT_TITLE')"
    @show="onShow"
    @hide="onHide"
  >
    <VForm
      ref="form"
      :schema="formSchema"
      :is-loading="isSaveLoading"
      :custom-errors="errors"
      reverse-buttons
      @change="onChange"
      @interacted="clearError"
      @submit="onSubmit"
    >
      <template #secondaryBtn>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_APPROVED, 'CHOOSE_DEPARTMENT_OK')"
          :loading="isSaveLoading"
          @click="confirmChanging"
        />
      </template>
    </VForm>
  </VDialog>
</template>
